import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '@/controllers/api';
import { Footer } from '@/ui/components/Footer';
import { SEO } from '../components/SEO';
import { SplitTitle } from '../components/SplitTitle';
import { Photo, Video, AstroEvent, RecentTarget, NewsItem } from '@/types';
import { lazyWithRetry } from '@/utils/lazyWithRetry';
import { useInView } from 'framer-motion';
import { Moon, CalendarDays, BellRing } from 'lucide-react';
import { SmartImage } from '@/ui/components/ui/SmartImage';
import { ScreenshotPlaceholder } from '@/ui/components/sentinel/ScreenshotPlaceholder';
import { useSiteSettings } from '@/hooks/useSiteSettings';

// Lazy loaded heavy components
const TestimonialsSection = lazyWithRetry(() => import('@/ui/components/TestimonialsSection').then(module => ({ default: module.TestimonialsSection })));
const NewsletterModal = lazyWithRetry(() => import('@/ui/components/NewsletterModal').then(module => ({ default: module.NewsletterModal })));

// Dynamic viewport loader for heavy sections
const LazySection: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    margin: '200px 0px 200px 0px', // Trigger before they enter the screen
  });

  return (
    <div ref={ref} className={className}>
      {inView ? <React.Suspense fallback={<div className="h-40 w-full animate-pulse bg-white/5 rounded-2xl" />}>{children}</React.Suspense> : <div className="h-[200px] w-full" />}
    </div>
  );
};

const GOOGLE_PLAY_BADGES_HOME: Record<string, string> = {
  fr: 'GetItOnGooglePlay_Badge_Web_color_French.svg',
  en: 'GetItOnGooglePlay_Badge_Web_color_English.svg',
  es: 'GetItOnGooglePlay_Badge_Web_color_Spanish.svg',
  de: 'GetItOnGooglePlay_Badge_Web_color_German.svg',
  it: 'GetItOnGooglePlay_Badge_Web_color_Italian.svg',
  zh: 'GetItOnGooglePlay_Badge_Web_color_Chinese-China.svg',
};
const GOOGLE_PLAY_HOME_URL = 'https://play.google.com/store/apps/details?id=fr.stellarskyimager.planner&utm_source=website&utm_medium=section&utm_campaign=home_android';
// [US 6.17] Liens vers la landing Sentinel avec UTM (mesurés côté landing via sentinel_landing_visit).
const SENTINEL_BANNER_URL = '/sentinel?utm_source=website&utm_medium=banner&utm_campaign=home_sentinel';
const SENTINEL_PROMO_URL = '/sentinel?utm_source=website&utm_medium=section&utm_campaign=home_sentinel';
const PROMO_START = new Date('2026-05-14T00:00:00');
const PROMO_END = new Date('2026-05-28T00:00:00');

// Bandeau parcours (juste avant le bandeau Sentinel) : 4 illustrations qui font comprendre
// en un coup d'oeil comment les outils s'enchainent. Le recit est construit autour du cycle
// Sentinel (alerte GO -> suivi de progression) : gate sur sentinelPublic au meme titre que
// le bandeau CTA juste en dessous, pas de variante "sans Sentinel".
// Nom d'outil affiche au-dessus du libelle d'action : "Planner"/"Sentinel" sont des noms
// de produit, jamais traduits (verifie sur sentinelBanner.title et horizon.title, identiques
// dans les 6 locales) -> pas une cle i18n, une constante. La couleur reprend le token
// "primary" du site (#00d4ff, cyan) pour Planner/Horizon et l'emerald deja utilise pour
// le bandeau Sentinel juste en dessous. L'etape 3 (prise de vue) n'a pas d'outil SSI --
// c'est le materiel personnel de l'utilisateur -- donc pas de deuxieme ligne pour elle.
const JOURNEY_STEPS = [
    { key: 'plan', img: '/images/home/journey-plan.webp', tool: 'Planner + Horizon', toolColor: 'text-primary' },
    { key: 'alert', img: '/images/home/journey-alert.webp', tool: 'Sentinel', toolColor: 'text-emerald-400' },
    { key: 'shoot', img: '/images/home/journey-shoot.webp', tool: null, toolColor: '' },
    { key: 'track', img: '/images/home/journey-track.webp', tool: 'Sentinel', toolColor: 'text-emerald-400' },
] as const;

const formatCount = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
  }
  return num.toString();
};

// Generates the scalloped border path for the badge
const generateScallopedPath = () => {
  const points = 40;
  const MathPI2 = Math.PI * 2;
  let path = "";
  for (let i = 0; i < points; i++) {
    const a1 = (i * MathPI2) / points;
    const a2 = ((i + 0.5) * MathPI2) / points;
    const a3 = ((i + 1) * MathPI2) / points;

    // Endpoints at inner radius
    const x1 = 100 + 94 * Math.cos(a1);
    const y1 = 100 + 94 * Math.sin(a1);

    // Control point at outer radius
    const x2 = 100 + 104 * Math.cos(a2);
    const y2 = 100 + 104 * Math.sin(a2);

    const x3 = 100 + 94 * Math.cos(a3);
    const y3 = 100 + 94 * Math.sin(a3);

    if (i === 0) path += `M ${x1} ${y1} `;
    path += `Q ${x2} ${y2} ${x3} ${y3} `;
  }
  return path + 'Z';
};

const MONTHS_FALLBACK = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];

const TARGET_COMMON_NAMES: Record<string, string> = {
  'M31': 'Galaxie d\'Andromède', 'M33': 'Galaxie du Triangle', 'M42': 'Nébuleuse d\'Orion',
  'M45': 'Pléiades', 'M51': 'Galaxie du Tourbillon', 'M27': 'Nébuleuse de l\'Haltère',
  'M57': 'Nébuleuse de l\'Anneau', 'M8': 'Nébuleuse de la Lagune', 'M16': 'Nébuleuse de l\'Aigle',
  'M20': 'Nébuleuse Trifide', 'M81': 'Galaxie de Bode', 'M82': 'Galaxie du Cigare',
  'M101': 'Galaxie du Moulinet', 'M63': 'Galaxie du Tournesol', 'M64': 'Œil Noir',
  'M74': 'Galaxie Fantôme', 'M78': 'Nébuleuse de M78', 'M13': 'Grand Amas d\'Hercule',
  'NGC 7000': 'Nébuleuse Amérique du Nord', 'NGC 6992': 'Nébuleuse du Voile',
  'NGC 2237': 'Nébuleuse de la Rosette', 'NGC 6888': 'Nébuleuse du Croissant',
  'NGC 7293': 'Nébuleuse de l\'Hélice', 'NGC 7331': 'NGC 7331',
  'IC 1805': 'Nébuleuse du Cœur', 'IC 1848': 'Nébuleuse de l\'Âme',
  'IC 5070': 'Nébuleuse du Pélican', 'IC 4628': 'Nébuleuse de la Crevette',
};


const ASTRO_EVENT_CFG: Record<string, { label: string; color: string; bg: string; emoji: string }> = {
  meteor_shower: { label: 'Pluie de météores', color: 'text-indigo-400', bg: 'from-indigo-900/60 to-slate-900/80', emoji: '🌠' },
  opposition: { label: 'Événement planétaire', color: 'text-cyan-400', bg: 'from-cyan-900/60 to-slate-900/80', emoji: '🪐' },
  eclipse: { label: 'Éclipse', color: 'text-purple-400', bg: 'from-purple-900/60 to-slate-900/80', emoji: '🌑' },
  conjunction: { label: 'Conjonction', color: 'text-pink-400', bg: 'from-pink-900/60 to-slate-900/80', emoji: '✨' },
  occultation: { label: 'Occultation', color: 'text-amber-400', bg: 'from-amber-900/60 to-slate-900/80', emoji: '🌒' },
  elongation: { label: 'Élongation max.', color: 'text-yellow-400', bg: 'from-yellow-900/60 to-slate-900/80', emoji: '🌟' },
  other: { label: 'Événement', color: 'text-white/50', bg: 'from-white/10 to-white/5', emoji: '⭐' },
};

const PLANET_IMAGES: Record<string, string> = {
  Jupiter: '/images/astro-events/jupiter.jpg',
  Saturn: '/images/astro-events/saturne.jpg',
  Saturne: '/images/astro-events/saturne.jpg',
  Mars: '/images/astro-events/mars.jpg',
  Neptune: '/images/astro-events/neptune.jpg',
  Uranus: '/images/astro-events/uranus.jpg',
  Venus: '/images/astro-events/venus.jpg',
  Mercury: '/images/astro-events/mercure.jpg',
  Mercure: '/images/astro-events/mercure.jpg',
};

const EVENT_TYPE_IMAGES: Record<string, string> = {
  meteor_shower: '/images/astro-events/meteor-shower.jpg',
  eclipse: '/images/astro-events/eclipse.jpg',
  conjunction: '/images/astro-events/conjunction.jpg',
  elongation:  '/images/astro-events/Elongation.jpg',
  occultation: '/images/astro-events/ocultation.jpg',
};

export const Home = () => {
  const { t, i18n } = useTranslation();
  const months = (t('home.cards.months', { returnObjects: true }) as string[]).length === 12
    ? (t('home.cards.months', { returnObjects: true }) as string[])
    : MONTHS_FALLBACK;
  const formatShortDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getDate()} ${months[d.getMonth()]}`;
  };
  const currentLang = i18n.language.split('-')[0];
  const getLocalizedString = (data: any, lang: string): string => {
    if (!data) return '';
    if (typeof data === 'string') return data;
    return data[lang] || data['fr'] || '';
  };
  const isPromo = new Date() >= PROMO_START && new Date() < PROMO_END;
  const isPromoComing = new Date() < PROMO_START;
  const [subscriberCount, setSubscriberCount] = useState<string>('...');
  const [videoCount, setVideoCount] = useState<string>('...');
  const [observationNights, setObservationNights] = useState<string>('...');
  const [plannedNights, setPlannedNights] = useState<string>('...');
  const [randomPhoto, setRandomPhoto] = useState<Photo | null>(null);
  const [featuredVideo, setFeaturedVideo] = useState<Video | null>(null);
  const [translationsReady, setTranslationsReady] = useState(false);
  const [isNewsletterModalOpen, setIsNewsletterModalOpen] = useState(false);
  const [upcomingEvents, setUpcomingEvents] = useState<AstroEvent[]>([]);
  const [recentTargets, setRecentTargets] = useState<RecentTarget[]>([]);
  // Vignettes dont l'image n'a pas pu être chargée (source astro indisponible) :
  // on bascule alors sur le dégradé, jamais sur le texte alt brut.
  const [brokenThumbs, setBrokenThumbs] = useState<Record<string, boolean>>({});
  const [recentNews, setRecentNews] = useState<NewsItem[]>([]);
  const [newsIndex, setNewsIndex] = useState(0);
  const [activeSlide, setActiveSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();
  // [Interrupteur Sentinel] Toutes les surfaces de promo Sentinel de la Home sont conditionnées à ce flag.
  const { sentinelPublic } = useSiteSettings();

  // Wait for translations to be ready and update on language change
  useEffect(() => {
    const testTranslation = t('home.seo.title');
    if (testTranslation !== 'home.seo.title' && i18n.isInitialized) {
      setTranslationsReady(true);
    } else {
      setTranslationsReady(false);
      const timer = setTimeout(() => {
        const retestTranslation = t('home.seo.title');
        if (retestTranslation !== 'home.seo.title' && i18n.isInitialized) {
          setTranslationsReady(true);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [t, i18n.isInitialized, i18n.language]);

  // Helper function to parse HTML-like placeholders in translation strings
  const parseTranslationHTML = React.useMemo(() => {
    return (text: string) => {
      if (!text) return null;

      const segments: React.ReactNode[] = [];
      const regex = /<(strong|em|link)>(.*?)<\/\1>/g;
      const matches = Array.from(text.matchAll(regex));

      let lastIndex = 0;

      matches.forEach((match, idx) => {
        if (match.index !== undefined && match.index > lastIndex) {
          segments.push(text.substring(lastIndex, match.index));
        }

        const tag = match[1];
        const content = match[2];
        const uniqueKey = `${tag}-${idx}-${content.substring(0, 10)}`;

        if (tag === 'strong') {
          segments.push(<strong key={uniqueKey}>{content}</strong>);
        } else if (tag === 'em') {
          segments.push(<em key={uniqueKey}>{content}</em>);
        } else if (tag === 'link') {
          let to = '/planner';
          const lowerContent = content.toLowerCase();

          if (lowerContent.includes('sentinel')) {
            to = '/sentinel';
          } else if (lowerContent.includes('tutoriel') || lowerContent.includes('tutorial') || lowerContent.includes('教程') || lowerContent.includes('dedicati')) {
            to = '/videos';
          } else if (lowerContent.includes('galerie') || lowerContent.includes('gallery') || lowerContent.includes('画廊') || lowerContent.includes('galería')) {
            to = '/galerie';
          } else if (lowerContent.includes('fov') || lowerContent.includes('champ')) {
            to = '/guide-fov';
          } else if (lowerContent.includes('filtre') || lowerContent.includes('filter') || lowerContent.includes('temps de pose') || lowerContent.includes('exposure time')) {
            to = '/guide-filtres';
          }
          segments.push(
            <Link
              key={uniqueKey}
              to={to}
              className="text-primary hover:underline decoration-primary/50 underline-offset-4 font-medium"
            >
              {content}
            </Link>
          );
        }

        if (match.index !== undefined) {
          lastIndex = match.index + match[0].length;
        }
      });

      if (lastIndex < text.length) {
        segments.push(text.substring(lastIndex));
      }

      return <>{segments}</>;
    };
  }, []);

  useEffect(() => {
    api.youtube.getStats().then(data => {
      if (data.subscriberCount) {
        setSubscriberCount(formatCount(data.subscriberCount));
      }
      if (data.videoCount) {
        setVideoCount(data.videoCount.toString());
      }
    }).catch(err => {
      console.warn('Failed to fetch YouTube stats:', err?.message || err);
      setSubscriberCount('250K+');
    });

    api.settings.get().then(settings => {
      if (settings && settings.observationNights) {
        setObservationNights(settings.observationNights);
      } else {
        setObservationNights('789');
      }
    }).catch(err => {
      console.warn('Failed to fetch observation nights settings:', err);
      setObservationNights('789');
    });

    api.planner.getStats().then(data => {
      if (data && data.plannedNights) {
        setPlannedNights(formatCount(data.plannedNights));
      }
    }).catch(() => {
      setPlannedNights('4.2K');
    });

    api.photos.list().then(photos => {
      const published = photos.filter(p => p.status === 'published');
      if (published.length > 0) {
        const randIndex = Math.floor(Math.random() * published.length);
        setRandomPhoto(published[randIndex]);

        const interval = setInterval(() => {
          const nextIndex = Math.floor(Math.random() * published.length);
          setRandomPhoto(published[nextIndex]);
        }, 30000);

        return () => clearInterval(interval);
      }
    }).catch(err => {
      console.warn('Failed to fetch photos for home rotation:', err);
    });

    api.videos.list().then(videos => {
      const visible = videos.filter(v => v.isVisible);
      const featured = visible.find(v => v.isFeatured) || visible[0];
      setFeaturedVideo(featured || null);
    }).catch(err => {
      console.warn('Failed to fetch videos for home teaser:', err);
    });

    api.astroEvents.listUpcoming(12).then(setUpcomingEvents).catch(() => { });

    api.planner.getRecentTargets().then(setRecentTargets).catch(() => { });

    api.news.list().then(list => {
      setRecentNews(list.slice(0, 9));
    }).catch(() => { });
  }, []);

  useEffect(() => {
    if (isHovered) return;
    const timer = setInterval(() => setActiveSlide(s => (s + 1) % toolSlides.length), 6000);
    return () => clearInterval(timer);
  }, [isHovered]);

  const toolSlides = [
    {
      route: '/planner',
      imageSrc: '/images/planner-teaser.webp',
      imageSrcSet: '/images/planner-teaser-mobile.webp 400w, /images/planner-teaser.webp 512w',
      badge: t('home.planner.badge'),
      title: t('home.planner.title'),
      description: t('home.planner.description'),
      cta: t('home.planner.cta'),
      ctaIcon: 'rocket_launch',
      featureBadge: t('home.planner.newFeatureBadge'),
      free: t('home.planner.free'),
    },
    {
      route: '/horizon-generator',
      imageSrc: '/images/horizon-teaser.jpg',
      imageSrcSet: undefined as string | undefined,
      badge: t('home.horizon.badge'),
      title: t('home.horizon.title'),
      description: t('home.horizon.description'),
      cta: t('home.horizon.cta'),
      ctaIcon: 'terrain',
      featureBadge: t('home.horizon.newFeatureBadge'),
      free: t('home.planner.free'),
    },
    // [US 6.17] 3e outil : SSI Sentinel (badge « nouveau », essai 30 j à la place du « gratuit »).
    // [Interrupteur Sentinel] Slide retiré du carrousel tant que Sentinel n'est pas ouvert au public
    // (la rotation `% length` et les dots `.map` s'adaptent automatiquement au nombre de slides).
    ...(sentinelPublic ? [{
      route: SENTINEL_PROMO_URL,
      imageSrc: null as string | null,
      imageSrcSet: undefined as string | undefined,
      badge: t('home.sentinelTool.badge'),
      title: t('home.sentinelTool.title'),
      description: t('home.sentinelTool.description'),
      cta: t('home.sentinelTool.cta'),
      ctaIcon: 'radar',
      featureBadge: t('home.sentinelTool.newFeatureBadge'),
      free: t('home.sentinelTool.trial'),
    }] : []),
  ];

  return (
    <div className="min-h-screen w-full relative flex flex-col">
      <SEO title={t('home.seo.title')} description={t('home.seo.description')} />

      {/* Background is now static in index.html for LCP optimization */}

      <div className="relative z-10 flex-grow pt-24 pb-12 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 animate-fade-in relative max-w-6xl mx-auto">
            <div className="relative z-10 mx-auto px-4">
              <a
                href={GOOGLE_PLAY_HOME_URL}
                target="_blank"
                rel="noreferrer"
                className="mb-6 flex items-center justify-between gap-3 px-4 py-2.5 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-sm hover:bg-white/10 hover:border-white/20 transition-all group animate-fade-in"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="material-symbols-outlined text-primary flex-shrink-0" style={{ fontSize: '18px' }}>android</span>
                  <span className="text-white/80 text-sm font-medium truncate">{t('home.androidApp.title')}</span>
                  {isPromo ? (
                    <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 bg-amber-400/15 border border-amber-400/30 rounded-full text-amber-300 text-[10px] font-bold uppercase tracking-wider flex-shrink-0">
                      <span className="material-symbols-outlined" style={{ fontSize: '11px' }}>local_offer</span>
                      {t('home.androidApp.pricePromo')} · {t('home.androidApp.promoLabel')}
                    </span>
                  ) : isPromoComing ? (
                    <span className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 bg-sky-400/15 border border-sky-400/30 rounded-full text-sky-300 text-[10px] font-bold uppercase tracking-wider flex-shrink-0">
                      <span className="material-symbols-outlined" style={{ fontSize: '11px' }}>upcoming</span>
                      {t('home.androidApp.promoComingDate')}
                    </span>
                  ) : (
                    <span className="hidden sm:inline text-white/40 text-xs flex-shrink-0">{t('home.androidApp.priceRegular')}</span>
                  )}
                </div>
                <img
                  src={`/images/Google%20Play%20Store/${GOOGLE_PLAY_BADGES_HOME[i18n.language.slice(0, 2)] ?? GOOGLE_PLAY_BADGES_HOME['en']}`}
                  alt={t('home.androidApp.cta')}
                  className="h-9 w-auto flex-shrink-0 group-hover:scale-105 transition-transform"
                />
              </a>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-4 drop-shadow-lg tracking-tight">
                <SplitTitle title={t('home.hero.title')} highlight={t('home.hero.titleHighlight')} highlightClassName="text-primary italic" />
              </h1>
              <p className="text-lg md:text-xl text-white/80 font-light leading-relaxed max-w-3xl mx-auto">
                {/* [Interrupteur Sentinel] Fermé : variante du sous-titre sans la clause Sentinel. */}
                {sentinelPublic ? t('home.hero.subtitle') : t('home.hero.subtitleClosed')}
              </p>
            </div>
          </div>

          {/* Bandeau parcours SSI : planifier -> alerte Sentinel -> photographier -> suivre.
              Texte en surimpression (pas de description sous l'image) pour rester compact en
              hauteur. Vignettes doublees (144/184px) : sur mobile la rangee ne tient plus sur
              une largeur d'ecran (4 x 144px depasse 390px), donc defilement horizontal au lieu
              d'empiler en 2x2 -- l'empilement aurait redonne la hauteur qu'on cherche a eviter.
              A partir de lg (assez large pour la rangee complete, ~870px), plus de scroll. */}
          {sentinelPublic && (
          <div className="mb-4 lg:max-w-5xl lg:mx-auto">
              <div
                  className="flex items-center gap-2 sm:gap-3 overflow-x-auto testimonial-scroll snap-x snap-mandatory px-4 lg:px-0 lg:overflow-visible lg:justify-center animate-fade-in"
                  aria-label={t('home.journey.title')}
              >
                  {JOURNEY_STEPS.map((step, i) => (
                      <React.Fragment key={step.key}>
                          <div
                              className="relative w-[144px] h-[144px] sm:w-[184px] sm:h-[184px] rounded-2xl overflow-hidden flex-shrink-0 border border-white/10 snap-center"
                              title={t(`home.journey.${step.key}.desc`)}
                          >
                              <SmartImage
                                  src={step.img}
                                  alt={t(`home.journey.${step.key}.alt`)}
                                  width={368}
                                  height={368}
                                  className="w-full h-full object-cover"
                              />
                              <div className="absolute inset-x-0 bottom-0 h-3/4 bg-gradient-to-t from-black/85 via-black/40 to-transparent pointer-events-none" />
                              {/* Ancre en bas : le libelle d'action garde toujours la meme position,
                                  que l'etape ait ou non un nom d'outil au-dessus (etape 3 n'en a pas). */}
                              <div className="absolute inset-x-1.5 bottom-1.5 sm:bottom-2 flex flex-col items-center gap-0.5">
                                  {/* Le nom d'outil est le texte le plus important de la vignette (demande
                                      explicite) : plus gros et plus lourd que le libelle d'action, qui passe
                                      au second plan. Etape 3 sans outil SSI : son libelle reste seul, au
                                      format "action" -- volontairement plus discret que les 3 autres. */}
                                  {step.tool && (
                                      <span className={`text-[13px] sm:text-[15px] font-black uppercase tracking-wide leading-tight truncate ${step.toolColor}`}>
                                          {step.tool}
                                      </span>
                                  )}
                                  <span className="text-center text-white/80 text-[9px] sm:text-[10px] font-semibold leading-tight truncate">
                                      {t(`home.journey.${step.key}.label`)}
                                  </span>
                              </div>
                          </div>
                          {i < JOURNEY_STEPS.length - 1 && (
                              <svg viewBox="0 0 24 16" className="w-6 sm:w-8 h-auto flex-shrink-0 text-white/25" aria-hidden="true">
                                  <path d="M2 8 H16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeDasharray="2 3" fill="none" />
                                  <path d="M12 3 L18 8 L12 13" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                              </svg>
                          )}
                      </React.Fragment>
                  ))}
              </div>
          </div>
          )}

          {/* [US 6.17] Bandeau annonce SSI Sentinel (remplace le bandeau Horizon Generator) */}
          {/* [Interrupteur Sentinel] Masqué tant que Sentinel n'est pas ouvert au public. */}
          {sentinelPublic && (
          <Link
            to={SENTINEL_BANNER_URL}
            className="mb-8 max-w-2xl mx-auto flex items-center gap-3 sm:gap-4 px-4 py-3.5 rounded-2xl bg-gradient-to-r from-emerald-400/10 to-black/40 border border-emerald-400/30 backdrop-blur-sm hover:from-emerald-400/15 hover:border-emerald-400/50 transition-all duration-300 group animate-fade-in"
            style={{ boxShadow: '0 0 30px rgba(34,211,165,0.28), 0 0 8px rgba(34,211,165,0.14)' }}
          >
            <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-400/15 border border-emerald-400/35 rounded-full text-emerald-300 text-[10px] font-black uppercase tracking-widest">
              <span className="material-symbols-outlined" style={{ fontSize: '11px' }}>new_releases</span>
              {t('home.sentinelBanner.badge')}
            </span>
            <div className="flex-1 min-w-0">
              <span className="text-white font-bold text-sm">{t('home.sentinelBanner.title')}</span>
              <span className="hidden sm:inline text-white/50 text-sm ml-2">— {t('home.sentinelBanner.desc')}</span>
              <p className="sm:hidden text-white/50 text-xs mt-0.5 truncate">{t('home.sentinelBanner.desc')}</p>
            </div>
            <span className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-400/15 border border-emerald-400/35 text-emerald-300 text-xs font-bold group-hover:bg-emerald-400/25 group-hover:border-emerald-400/55 transition-all whitespace-nowrap">
              {t('home.sentinelBanner.cta')}
              <span className="material-symbols-outlined group-hover:translate-x-0.5 transition-transform" style={{ fontSize: '14px' }}>arrow_forward</span>
            </span>
          </Link>
          )}

          {/* Section dynamique — 3 cards riches */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.35fr_1fr] gap-4 mb-8 animate-fade-in delay-1">

            {/* ── Card 1 : Cibles récentes ───────────────────────────── */}
            <div className="rounded-2xl bg-black/40 border border-white/10 backdrop-blur-md flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/10 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary" style={{ fontSize: 16 }}>explore</span>
                  <span className="text-white font-bold text-sm tracking-tight">{t('home.cards.recentTargets.title')}</span>
                </div>
              </div>

              <div className="flex-1 divide-y divide-white/5 overflow-hidden">
                {recentTargets.length > 0 ? recentTargets.slice(0, 4).map((target, i) => (
                  <div key={i} className="flex items-center gap-3.5 px-4 py-2">
                    <div className="w-[88px] h-[58px] rounded-xl overflow-hidden flex-shrink-0 bg-black/60">
                      {target.imageUrl && !brokenThumbs[target.name]
                        ? <img
                          src={target.imageUrl}
                          alt={target.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={() => setBrokenThumbs(prev => ({ ...prev, [target.name]: true }))}
                        />
                        : <div className="w-full h-full bg-gradient-to-br from-primary/25 to-cyan-950 flex items-center justify-center">
                          <span className="text-primary/40 text-[10px] font-black">{target.name.slice(0, 4)}</span>
                        </div>
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-bold text-[15px] leading-tight">{target.catalogName ?? target.name}</div>
                      <div className="text-white/40 text-xs mt-0.5 truncate">
                        {target.catalogName && target.catalogName.replace(/\s/g, '') !== target.name.replace(/\s/g, '')
                          ? target.name
                          : TARGET_COMMON_NAMES[target.catalogName ?? target.name] ?? ''}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                      <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/25 text-cyan-400 text-[10px] font-bold whitespace-nowrap">
                        <span className="material-symbols-outlined" style={{ fontSize: 11 }}>check_circle</span>
                        {t('home.cards.recentTargets.exported')}
                      </div>
                      <div className="text-white/35 text-[10px]">{formatShortDate(target.exportedAt)}</div>
                    </div>
                  </div>
                )) : Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center gap-3.5 px-4 py-2">
                    <div className="w-[88px] h-[58px] rounded-xl bg-white/5 animate-pulse flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-28 bg-white/5 rounded animate-pulse" />
                      <div className="h-2.5 w-20 bg-white/5 rounded animate-pulse" />
                    </div>
                    <div className="space-y-1.5">
                      <div className="h-5 w-16 bg-white/5 rounded-full animate-pulse" />
                      <div className="h-2.5 w-10 ml-auto bg-white/5 rounded animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>

              <Link to="/planner" className="flex items-center justify-center gap-1.5 px-5 py-3 border-t border-white/10 text-primary/70 text-xs font-bold hover:text-primary transition-colors group flex-shrink-0">
                {t('home.cards.recentTargets.goToPlanner')}
                <span className="material-symbols-outlined text-sm group-hover:translate-x-1 transition-transform">arrow_forward</span>
              </Link>
            </div>

            {/* ── Card 2 : Actualités ────────────────────────────────── */}
            <div className="rounded-2xl bg-black/40 border border-white/10 backdrop-blur-md flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/10 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary" style={{ fontSize: 16 }}>article</span>
                  <span className="text-white font-bold text-sm tracking-tight">{t('home.cards.news.title')}</span>
                </div>
                <Link to="/actualites" className="text-primary text-[11px] font-bold hover:text-white transition-colors">{t('home.cards.news.viewAll')}</Link>
              </div>

              {recentNews.length > 0 ? (() => {
                const page = recentNews.slice(newsIndex * 3, newsIndex * 3 + 3);
                const totalPages = Math.ceil(recentNews.length / 3);
                return (
                  <>
                    <div className="grid grid-cols-3 gap-3 p-4 flex-1">
                      {page.map(n => (
                        <a
                          key={n.id}
                          href={n.link || '/actualites'}
                          target={n.link ? '_blank' : undefined}
                          rel={n.link ? 'noreferrer' : undefined}
                          className="flex flex-col group rounded-xl overflow-hidden bg-black/30 border border-white/5 hover:border-white/20 transition-colors"
                        >
                          <div className="relative h-[140px] flex-shrink-0 bg-black/60 overflow-hidden">
                            {n.imageUrl
                              ? <img src={n.imageUrl} alt={getLocalizedString(n.title_i18n ?? n.title, currentLang)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                              : <div className="w-full h-full bg-gradient-to-br from-primary/10 to-cyan-950 flex items-center justify-center">
                                <span className="material-symbols-outlined text-primary/30 text-3xl">article</span>
                              </div>
                            }
                          </div>
                          <div className="flex flex-col flex-1 p-3">
                            <div className="flex items-center gap-1 text-white/40 text-[10px] font-medium mb-1.5">
                              <span className="material-symbols-outlined" style={{ fontSize: 11 }}>calendar_today</span>
                              {formatShortDate(n.date)}
                            </div>
                            <div className="text-white font-bold text-[12px] leading-snug line-clamp-2 mb-1.5">{getLocalizedString(n.title_i18n ?? n.title, currentLang)}</div>
                            <div className="text-white/50 text-[11px] leading-relaxed line-clamp-2 mb-2 flex-1">{getLocalizedString(n.content_i18n ?? n.content, currentLang)}</div>
                            <div className="text-primary text-[11px] font-bold group-hover:translate-x-0.5 transition-transform inline-flex items-center gap-0.5">
                              {t('home.cards.news.readMore')} <span className="material-symbols-outlined" style={{ fontSize: 12 }}>arrow_forward</span>
                            </div>
                          </div>
                        </a>
                      ))}
                    </div>
                    {totalPages > 1 && (
                      <div className="flex items-center justify-center gap-2 pb-4">
                        {Array.from({ length: totalPages }).map((_, i) => (
                          <button
                            key={i}
                            onClick={() => setNewsIndex(i)}
                            className={`rounded-full transition-all duration-200 ${i === newsIndex ? 'w-5 h-1.5 bg-primary' : 'w-1.5 h-1.5 bg-white/25 hover:bg-white/50'}`}
                            aria-label={t('common.page', { number: i + 1 })}
                            aria-current={i === newsIndex ? 'true' : undefined}
                          />
                        ))}
                      </div>
                    )}
                  </>
                );
              })() : (
                <div className="grid grid-cols-3 gap-3 p-4">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="rounded-xl overflow-hidden bg-black/30 border border-white/5">
                      <div className="h-[140px] bg-white/5 animate-pulse" />
                      <div className="p-3 space-y-2">
                        <div className="h-2 w-14 bg-white/5 rounded animate-pulse" />
                        <div className="h-3 w-full bg-white/5 rounded animate-pulse" />
                        <div className="h-3 w-4/5 bg-white/5 rounded animate-pulse" />
                        <div className="h-2.5 w-full bg-white/5 rounded animate-pulse" />
                        <div className="h-2.5 w-3/4 bg-white/5 rounded animate-pulse" />
                        <div className="h-3 w-16 bg-primary/20 rounded animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Card 3 : Événements Astro ──────────────────────────── */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsNewsletterModalOpen(true)}
                // Mobile : badge réduit et moins décalé — le SVG est en overflow-visible, ses
                // cercles décoratifs sortaient du cadre et débordaient de l'écran (scroll parasite).
                className="absolute -top-20 -right-2 md:-right-8 z-[100] transition-all duration-300 hover:scale-105 group cursor-pointer origin-center floating-badge"
                title={t('home.newsletterBadge.description')}
              >
                <div className="relative animate-floating drop-shadow-[0_15px_25px_rgba(0,0,0,0.5)] group-hover:drop-shadow-[0_20px_40px_rgba(239,68,68,0.4)] transition-all duration-500 w-[140px] h-[140px] md:w-[180px] md:h-[180px]">
                  <svg viewBox="0 0 200 200" className="w-full h-full text-cyan-100 overflow-visible">
                    <defs>
                      <radialGradient id="badgeGrad" cx="50%" cy="30%" r="70%" fx="50%" fy="30%">
                        <stop offset="0%" stopColor="#f87171" />
                        <stop offset="60%" stopColor="#dc2626" />
                        <stop offset="100%" stopColor="#7f1d1d" />
                      </radialGradient>
                      <linearGradient id="gloss" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="rgba(255,255,255,0.45)" />
                        <stop offset="40%" stopColor="rgba(255,255,255,0.1)" />
                        <stop offset="40.1%" stopColor="rgba(255,255,255,0)" />
                        <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                      </linearGradient>
                      <path id="curveTop" d="M 35 100 A 65 65 0 0 1 165 100" />
                      <path id="curveBottom" d="M 35 100 A 65 65 0 0 0 165 100" />
                      <filter id="glow" x="-20%" y="-20%" width="140%" height="140%">
                        <feGaussianBlur stdDeviation="3" result="blur" />
                        <feComposite in="SourceGraphic" in2="blur" operator="over" />
                      </filter>
                      <filter id="shadow">
                        <feDropShadow dx="1" dy="2" stdDeviation="2" floodOpacity="0.5" />
                      </filter>
                    </defs>
                    <g transform="rotate(30 100 100) scale(0.96) translate(4 4)">
                      <path d={generateScallopedPath()} fill="url(#badgeGrad)" stroke="#7f1d1d" strokeWidth="1.5" />
                    </g>
                    <g transform="rotate(30 100 100)">
                      <circle cx="100" cy="100" r="82" fill="none" stroke="currentColor" strokeWidth="1.5" strokeOpacity="0.9" />
                      <circle cx="100" cy="100" r="77" fill="none" stroke="currentColor" strokeWidth="0.75" strokeOpacity="0.7" />
                      <circle cx="100" cy="100" r="77" fill="url(#gloss)" />
                      <text className="font-bold tracking-widest fill-current drop-shadow-md" fontSize="9" letterSpacing="1.2">
                        <textPath href="#curveTop" startOffset="50%" textAnchor="middle">
                          ★ {t('home.newsletterBadge.top')} ★
                        </textPath>
                      </text>
                      <text className="font-bold tracking-widest fill-current drop-shadow-md" fontSize="9" letterSpacing="1.2">
                        <textPath href="#curveBottom" startOffset="50%" textAnchor="middle">
                          ★ {t('home.newsletterBadge.bottom')} ★
                        </textPath>
                      </text>
                      <g transform="translate(100, 100)" filter="url(#shadow)">
                        <text x="0" y="-4" textAnchor="middle" className="font-black fill-[#fffaeb]" fontSize="24">
                          {t('home.newsletterBadge.middle').split('\n')[0]}
                        </text>
                        <text x="0" y="20" textAnchor="middle" className="font-black fill-[#fffaeb]" fontSize="26">
                          {t('home.newsletterBadge.middle').split('\n')[1] || ''}
                        </text>
                      </g>
                    </g>
                    <g transform="translate(160, 45) scale(0.6)">
                      <path d="M0,0 Q10,0 10,-10 Q10,0 20,0 Q10,0 10,10 Q10,0 0,0" fill="#fff" filter="url(#glow)" className="animate-pulse" />
                    </g>
                    <g transform="translate(30, 150) scale(0.4)">
                      <path d="M0,0 Q10,0 10,-10 Q10,0 20,0 Q10,0 10,10 Q10,0 0,0" fill="#fff" filter="url(#glow)" className="animate-pulse" style={{ animationDelay: '0.7s' }} />
                    </g>
                  </svg>
                </div>
              </button>
              <div className="rounded-2xl bg-black/40 border border-white/10 backdrop-blur-md flex flex-col overflow-hidden">
                <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/10 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-primary" style={{ fontSize: 16 }}>calendar_month</span>
                    <span className="text-white font-bold text-sm tracking-tight">{t('home.cards.events.title')}</span>
                  </div>
                </div>

                <div className="max-h-[300px] px-4 py-1 relative overflow-y-auto" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(32,129,147,0.3) transparent' }}>
                  {/* Ligne timeline — centrée sur les dots : 16px padding + 40px date + 12px gap + 5px demi-dot = 73px */}
                  <div className="absolute left-[73px] top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-white/15 to-transparent" />
                  {upcomingEvents.length > 0 ? upcomingEvents.map((ev, idx) => {
                    const d = new Date(ev.date.slice(0, 10) + 'T12:00:00');
                    const month = months[d.getMonth()].toUpperCase();
                    const day = d.getDate();
                    const year = d.getFullYear();
                    const cfg = ASTRO_EVENT_CFG[ev.type] ?? ASTRO_EVENT_CFG.other;
                    const planetKey = ev.name.match(/(Jupiter|Saturn|Saturne|Mars|Neptune|Uranus|Venus|Mercury|Mercure)/i)?.[1] ?? '';
                    const pKey = planetKey.charAt(0).toUpperCase() + planetKey.slice(1).toLowerCase();
                    const evImgUrl = (ev.type === 'opposition' && PLANET_IMAGES[pKey])
                      ? PLANET_IMAGES[pKey]
                      : EVENT_TYPE_IMAGES[ev.type] ?? null;
                    const peakD = ev.peak_date ? new Date(ev.peak_date.slice(0, 10) + 'T12:00:00') : null;
                    const dateStr = peakD && peakD.getTime() !== d.getTime()
                      ? `${day}–${peakD.getDate()} ${months[d.getMonth()]} ${year}`
                      : `${day} ${months[d.getMonth()]} ${year}`;
                    return (
                      <div key={ev.id} className="flex items-center gap-3 py-2.5 border-b border-white/5 last:border-0">
                        <div className="w-10 flex-shrink-0 text-right">
                          <div className="text-white/40 text-[9px] font-bold tracking-wider">{month}</div>
                          <div className="text-white font-black text-[22px] leading-none">{day}</div>
                        </div>
                        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 relative z-10 transition-all ${idx === 0
                          ? 'border-2 border-primary bg-primary/20 ring-2 ring-primary/20'
                          : 'border border-slate-500/60 bg-slate-900'
                          }`} />
                        <div className={`w-9 h-9 rounded-full flex-shrink-0 overflow-hidden bg-gradient-to-br ${cfg.bg}`}>
                          {evImgUrl
                            ? <img src={evImgUrl} alt={ev.name} className="w-full h-full object-cover" loading="lazy" />
                            : <span className="flex items-center justify-center w-full h-full text-lg">{cfg.emoji}</span>
                          }
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-white font-bold text-[12px] leading-tight truncate">{getLocalizedString(ev.name_i18n ?? { fr: ev.name }, currentLang)}</div>
                          <div className="text-white/35 text-[10px] mt-0.5">{dateStr}</div>
                          <div className={`text-[10px] font-bold mt-0.5 ${cfg.color}`}>{t(`home.cards.events.types.${ev.type}`, t('home.cards.events.types.other'))}</div>
                        </div>
                      </div>
                    );
                  }) : Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} className="flex items-center gap-3 py-2.5 border-b border-white/5 last:border-0">
                      <div className="w-10 space-y-1 text-right">
                        <div className="h-2 w-6 ml-auto bg-white/5 rounded animate-pulse" />
                        <div className="h-5 w-5 ml-auto bg-white/5 rounded animate-pulse" />
                      </div>
                      <div className="w-2.5 h-2.5 rounded-full border border-slate-600/50 bg-slate-900 flex-shrink-0" />
                      <div className="w-9 h-9 rounded-full bg-white/5 animate-pulse flex-shrink-0" />
                      <div className="flex-1 space-y-1">
                        <div className="h-2.5 w-28 bg-white/5 rounded animate-pulse" />
                        <div className="h-2 w-20 bg-white/5 rounded animate-pulse" />
                        <div className="h-2 w-16 bg-white/5 rounded animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>

                <Link to="/evenements" className="flex items-center justify-center gap-1.5 px-5 py-3 border-t border-white/10 text-primary/70 text-xs font-bold hover:text-primary transition-colors group flex-shrink-0">
                  {t('home.cards.events.viewAll')}
                  <span className="material-symbols-outlined text-sm group-hover:translate-x-1 transition-transform">arrow_forward</span>
                </Link>
              </div>
            </div>

          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8">
            <div
              className="lg:col-span-12 xl:col-span-8 group relative rounded-2xl border border-white/10 bg-black/40 backdrop-blur-md transition-all duration-500 hover:border-primary/50 hover:-translate-y-4 hover:scale-[1.02] shadow-floating overflow-hidden flex flex-col min-h-[420px] cursor-pointer"
              onClick={() => navigate(toolSlides[activeSlide].route)}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              {/* Background images — crossfade */}
              <div className="absolute inset-0 z-0">
                <div className="absolute inset-0 bg-gradient-to-tr from-black/90 via-black/20 to-transparent z-10" />
                <SmartImage
                  src="/images/planner-teaser.webp"
                  srcSet="/images/planner-teaser-mobile.webp 500w, /images/planner-teaser.webp 1254w"
                  sizes="(max-width: 768px) 400px, 900px"
                  alt="SSI Session Planner Teaser"
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 group-hover:scale-105 ${activeSlide === 0 ? 'opacity-60' : 'opacity-0'}`}
                  isLcp={true}
                  width={1254}
                  height={1254}
                />
                <SmartImage
                  src="/images/horizon-teaser.jpg"
                  alt="SSI Horizon Generator Teaser"
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 group-hover:scale-105 ${activeSlide === 1 ? 'opacity-60' : 'opacity-0'}`}
                  isLcp={false}
                  width={512}
                  height={512}
                />
                {/* [US 6.17] Fond du slide Sentinel — teaser produit (fiche projet M31 + trajectoire). */}
                {/* [Interrupteur Sentinel] Masqué avec le slide (cf. toolSlides) tant que Sentinel est fermé. */}
                {sentinelPublic && (
                <SmartImage
                  src="/images/Sentinel/Slide_Sentinel.webp"
                  alt="SSI Sentinel — suivi de projet et trajectoire de la nuit"
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 group-hover:scale-105 ${activeSlide === 2 ? 'opacity-60' : 'opacity-0'}`}
                  isLcp={false}
                  width={1672}
                  height={941}
                />
                )}
              </div>

              {/* Feature badge */}
              <div className="absolute top-4 right-4 z-30 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-400/90 text-black text-xs font-black uppercase tracking-wider shadow-lg animate-bounce">
                <span className="material-symbols-outlined text-sm" style={{ fontSize: '14px' }}>new_releases</span>
                {toolSlides[activeSlide].featureBadge}
              </div>

              {/* Slide content — fade on change */}
              <div key={activeSlide} className="relative z-20 p-8 h-full flex flex-col justify-end flex-grow animate-fade-in">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 border border-primary/30 text-primary text-xs font-bold uppercase tracking-widest mb-4 w-fit animate-pulse">
                  {toolSlides[activeSlide].badge}
                </div>
                <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 group-hover:text-primary transition-colors italic drop-shadow-lg">
                  {toolSlides[activeSlide].title}
                </h2>
                <p className="text-white/80 text-lg max-w-xl mb-8 font-light drop-shadow-md">
                  {toolSlides[activeSlide].description}
                </p>
                <div className="flex flex-wrap gap-4">
                  <div className="px-8 py-4 bg-primary text-[#0f2023] font-black rounded-lg group-hover:bg-cyan-400 transition transform hover:-translate-y-1 hover:scale-105 shadow-xl shadow-primary/30 flex items-center gap-2 uppercase tracking-tighter">
                    <span className="material-symbols-outlined">{toolSlides[activeSlide].ctaIcon}</span>
                    {toolSlides[activeSlide].cta}
                  </div>
                  <div className="flex items-center gap-2 text-white/70 text-sm italic py-2">
                    <span className="material-symbols-outlined text-sm">check_circle</span>
                    {toolSlides[activeSlide].free}
                  </div>
                </div>
              </div>

              {/* Navigation dots */}
              <div
                className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex gap-2"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                {toolSlides.map((slide, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveSlide(i)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${activeSlide === i ? 'w-6 bg-primary' : 'w-1.5 bg-white/30 hover:bg-white/60'}`}
                    // Le titre du slide est déjà traduit : pas de clé i18n supplémentaire.
                    aria-label={slide.title}
                    aria-current={activeSlide === i ? 'true' : undefined}
                  />
                ))}
              </div>

              <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            </div>

            <div className="lg:col-span-12 xl:col-span-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-1 gap-6 lg:gap-8">
              <Link to="/galerie" className="group relative overflow-hidden rounded-2xl border border-white/10 bg-black/40 backdrop-blur-md transition-all duration-300 hover:border-white/30 hover:-translate-y-4 shadow-floating flex flex-col min-h-[220px]">
                <div className="absolute inset-0 z-0">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent z-10" />
                  {randomPhoto ? (
                    <SmartImage
                      src={randomPhoto.thumbnailUrl || randomPhoto.imageUrl}
                      alt={randomPhoto.title}
                      className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-110 transition-transform duration-700"
                      isLcp={false}
                      width={400} // Placeholder size for gallery thumbnails
                      height={300}
                    />
                  ) : (
                    <div className="absolute inset-0 bg-white/5 animate-pulse" />
                  )}
                </div>
                <div className="relative z-20 p-6 h-full flex flex-col justify-end flex-grow">
                  <h3 className="text-xl font-bold text-white mb-1 group-hover:text-primary transition-colors">{t('home.gallery.title')}</h3>
                  <p className="text-white/70 text-sm mb-4 line-clamp-2 italic font-light">
                    {randomPhoto ? t('home.gallery.currentlyFeaturing', { title: randomPhoto.title }) : t('home.gallery.fallbackText')}
                  </p>
                  <span className="text-primary text-xs font-bold uppercase tracking-widest flex items-center gap-2 group-hover:translate-x-2 transition-transform">
                    {t('home.gallery.cta')} <span className="material-symbols-outlined text-sm">arrow_forward</span>
                  </span>
                </div>
              </Link>

              <Link to="/videos" className="group relative overflow-hidden rounded-2xl border border-white/10 bg-black/40 backdrop-blur-md transition-all duration-300 hover:border-white/30 hover:-translate-y-4 shadow-floating flex flex-col min-h-[220px]">
                <div className="absolute inset-0 z-0">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent z-10" />
                  {featuredVideo ? (
                    <SmartImage
                      src={featuredVideo.thumbnailUrl}
                      alt={featuredVideo.title}
                      className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:scale-110 transition-transform duration-700"
                      width={480} // Standard YouTube HQ thumbnail size
                      height={360}
                    />
                  ) : (
                    <div className="absolute inset-0 bg-white/5 animate-pulse" />
                  )}
                </div>
                <div className="relative z-20 p-6 h-full flex flex-col justify-end flex-grow">
                  <div className="absolute top-4 right-4 group-hover:rotate-12 transition-transform duration-300">
                    <span className="material-symbols-outlined text-primary text-4xl opacity-80 group-hover:opacity-100 group-hover:scale-125 transition-all drop-shadow-[0_0_15px_rgba(6,182,212,0.6)]">play_circle</span>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-1 group-hover:text-primary transition-colors">{t('home.videos.title')}</h3>
                  <p className="text-white/70 text-sm mb-4 line-clamp-2 italic font-light">
                    {t('home.videos.fallbackText')}
                  </p>
                  <span className="text-primary text-xs font-bold uppercase tracking-widest flex items-center gap-2 group-hover:translate-x-2 transition-transform">
                    {t('home.videos.cta')} <span className="material-symbols-outlined text-sm">arrow_forward</span>
                  </span>
                </div>
              </Link>
            </div>
          </div>

          {/* [US 6.17] Section promo SSI Sentinel — GO/NO-GO quotidien, captures (placeholders
              partagés avec la landing, à remplacer avant prod), CTA avec UTM home_sentinel. */}
          {/* [Interrupteur Sentinel] Section entière masquée tant que Sentinel n'est pas ouvert au public. */}
          {sentinelPublic && (
          <section className="mt-16 mb-20 animate-fade-in delay-2 px-4">
            <div className="relative overflow-hidden rounded-3xl border border-emerald-400/20 bg-gradient-to-br from-black/50 via-emerald-950/15 to-black/50 backdrop-blur-md p-8 md:p-12">
              <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(800px 300px at 50% 0%, rgba(34,211,165,.08), transparent 60%)' }} />
              <div className="relative z-10">
                <div className="text-center mb-10">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-400/15 border border-emerald-400/30 text-emerald-300 text-xs font-bold uppercase tracking-widest mb-4">
                    <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>radar</span>
                    {t('home.sentinelPromo.badge')}
                  </div>
                  <h2 className="text-3xl md:text-5xl font-black text-white mb-4 tracking-tighter uppercase italic">
                    <SplitTitle title={t('home.sentinelPromo.title')} highlight={t('home.sentinelPromo.titleHighlight')} highlightClassName="text-emerald-300" />
                  </h2>
                  <p className="text-white/70 font-light leading-relaxed max-w-3xl mx-auto">
                    {t('home.sentinelPromo.desc')}
                  </p>
                </div>

                {/* Captures — légendes réutilisées de la landing (sentinelLanding.shots.*, zéro duplication) */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
                  {[
                    { key: 'tonight', icon: <Moon size={22} />, img: '/images/Sentinel/Ce_Soir.png', pos: 'left' },
                    { key: 'calendar', icon: <CalendarDays size={22} />, img: '/images/Sentinel/Calendrier14j.png', pos: 'left' },
                    { key: 'alert', icon: <BellRing size={22} />, img: '/images/Sentinel/Push_Sentinel.png' },
                  ].map(s => (
                    <figure key={s.key} className="m-0">
                      <ScreenshotPlaceholder caption={t('sentinelLanding.shots.placeholder')} icon={s.icon} src={s.img}
                        objectPosition={s.pos} alt={t(`sentinelLanding.shots.${s.key}.title`)} />
                      <figcaption className="mt-2.5 text-center">
                        <div className="text-[13px] font-semibold text-white">{t(`sentinelLanding.shots.${s.key}.title`)}</div>
                        <div className="text-[12px] text-white/50 mt-0.5">{t(`sentinelLanding.shots.${s.key}.desc`)}</div>
                      </figcaption>
                    </figure>
                  ))}
                </div>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <Link
                    to={SENTINEL_PROMO_URL}
                    className="px-8 py-4 bg-emerald-400 text-[#07211a] font-black rounded-lg hover:bg-emerald-300 transition transform hover:-translate-y-1 hover:scale-105 shadow-xl shadow-emerald-400/25 flex items-center gap-2 uppercase tracking-tighter"
                  >
                    <span className="material-symbols-outlined">radar</span>
                    {t('home.sentinelPromo.cta')}
                  </Link>
                  <div className="flex items-center gap-2 text-white/70 text-sm italic">
                    <span className="material-symbols-outlined text-sm text-emerald-300">check_circle</span>
                    {t('home.sentinelPromo.trial')}
                  </div>
                </div>
              </div>
            </div>
          </section>
          )}

          {/* [US 6.18] Tour produit migré sur /decouverte-planner — carte compacte de renvoi */}
          <section className="mt-12 mb-20 animate-fade-in delay-1 px-4">
            <Link
              to="/decouverte-planner"
              className="group block max-w-5xl mx-auto rounded-3xl bg-white/5 border border-white/10 backdrop-blur-sm hover:border-primary/40 hover:bg-white/10 transition-all overflow-hidden"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 items-center">
                <div className="relative aspect-video md:aspect-auto md:h-full bg-black/40 border-b md:border-b-0 md:border-r border-white/10">
                  <SmartImage
                    src="/images/home/planner-step-selection.webp"
                    alt={t('home.discoverPlanner.title')}
                    className="w-full h-full object-contain opacity-90 group-hover:opacity-100 transition-opacity duration-500 p-4"
                    width={512}
                    height={333}
                  />
                </div>
                <div className="p-8 md:p-10">
                  <h2 className="text-2xl md:text-3xl font-black text-white mb-3 tracking-tighter uppercase italic group-hover:text-primary transition-colors">
                    {t('home.discoverPlanner.title')}
                  </h2>
                  <p className="text-white/70 text-sm leading-relaxed font-light mb-6">
                    {t('home.discoverPlanner.desc')}
                  </p>
                  <span className="inline-flex items-center gap-2 text-primary font-bold text-sm uppercase tracking-widest">
                    {t('home.discoverPlanner.cta')}
                    <span className="material-symbols-outlined text-base group-hover:translate-x-1 transition-transform">arrow_forward</span>
                  </span>
                </div>
              </div>
            </Link>
          </section>

          {/* [US 6.16/6.17] Teaser 3 témoignages + lien vers /temoignages — remonté près de la
              conversion (la section « profils » a été migrée sur /decouverte-planner). */}
          <LazySection className="mt-16 mb-20">
            <TestimonialsSection variant="teaser" />
          </LazySection>

          <section className="mt-20 mb-12 animate-fade-in delay-4">
            <div className="bg-black/20 backdrop-blur-sm border border-white/5 rounded-3xl p-8 md:p-12">
              <h2 className="text-2xl md:text-4xl font-bold text-white mb-8 border-l-4 border-primary pl-6">
                {t('home.seo.sectionTitle')}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 text-white/70 leading-relaxed font-light">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-primary font-bold text-lg mb-2 uppercase tracking-wider">{t('home.seo.plannerTitle')}</h3>
                    <p>{parseTranslationHTML(t('home.seo.plannerText'))}</p>
                  </div>
                  <div>
                    <h3 className="text-primary font-bold text-lg mb-2 uppercase tracking-wider">{t('home.seo.masteryTitle')}</h3>
                    <p>{parseTranslationHTML(t('home.seo.masteryText'))}</p>
                  </div>
                </div>
                <div className="space-y-6">
                  <div>
                    <h3 className="text-primary font-bold text-lg mb-2 uppercase tracking-wider">{t('home.seo.processingTitle')}</h3>
                    <p>{parseTranslationHTML(t('home.seo.processingText'))}</p>
                  </div>
                  <div>
                    <h3 className="text-primary font-bold text-lg mb-2 uppercase tracking-wider">{t('home.seo.communityTitle')}</h3>
                    <p>{parseTranslationHTML(t('home.seo.communityText'))}</p>
                  </div>
                </div>
              </div>
              {/* [US 6.17] 5e paragraphe : alertes météo GO/NO-GO (SSI Sentinel) */}
              {/* [Interrupteur Sentinel] Masqué tant que Sentinel n'est pas ouvert au public. */}
              {sentinelPublic && (
              <div className="mt-12 text-white/70 leading-relaxed font-light">
                <h3 className="text-primary font-bold text-lg mb-2 uppercase tracking-wider">{t('home.seo.sentinelTitle')}</h3>
                <p>{parseTranslationHTML(t('home.seo.sentinelText'))}</p>
              </div>
              )}
            </div>
          </section>

          <section className="mt-12 mb-20 animate-fade-in delay-2 px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { to: '/guide-planification', titleKey: 'home.guides.planning.title', icon: 'event_note', labelKey: 'home.guides.planning.label' },
                { to: '/guide-fov', titleKey: 'home.guides.fov.title', icon: 'center_focus_strong', labelKey: 'home.guides.fov.label' },
                { to: '/guide-filtres', titleKey: 'home.guides.filters.title', icon: 'filter_alt', labelKey: 'home.guides.filters.label' }
              ].map((guide, idx) => (
                <Link key={idx} to={guide.to} className="bg-white/5 border border-white/10 p-6 rounded-2xl flex items-center justify-between group hover:border-primary/50 transition-all">
                  <div className="flex items-center gap-4">
                    <span className="material-symbols-outlined text-primary text-2xl group-hover:scale-110 transition-transform">{guide.icon}</span>
                    <div>
                      <h4 className="text-white font-bold text-sm tracking-tight">{t(guide.titleKey)}</h4>
                      <p className="text-white/70 text-[10px] uppercase font-black tracking-widest">{t(guide.labelKey)}</p>
                    </div>
                  </div>
                  <span className="material-symbols-outlined text-white/20 group-hover:text-primary transition-colors">arrow_forward</span>
                </Link>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link to="/guides" className="text-white/70 hover:text-primary text-xs font-bold uppercase tracking-[0.2em] transition-colors flex items-center justify-center gap-2">
                {t('home.guides.viewAll')} <span className="material-symbols-outlined text-sm">open_in_new</span>
              </Link>
            </div>
          </section>

          <section className="mb-24 animate-fade-in delay-3">
            <h2 className="text-3xl md:text-5xl font-black text-white text-center mb-4 tracking-tighter uppercase italic px-4">
              <SplitTitle title={t('home.whySSI.title')} highlight={t('home.whySSI.titleHighlight')} />
            </h2>
            <p className="text-white/70 text-center mb-16 uppercase tracking-[0.3em] font-bold text-xs">
              {t('home.whySSI.subtitle')}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 px-4">
              {[
                {
                  icon: 'language',
                  img: '/images/home/Multilingue.jpg',
                  titleKey: 'home.whySSI.multilingual.title',
                  descKey: 'home.whySSI.multilingual.desc',
                  color: 'primary'
                },
                {
                  icon: 'calculate',
                  img: '/images/home/Expertise_scientifique.jpg',
                  titleKey: 'home.whySSI.scientific.title',
                  descKey: 'home.whySSI.scientific.desc',
                  color: 'cyan'
                },
                {
                  icon: 'travel_explore',
                  img: '/images/home/Exports_universels.jpg',
                  titleKey: 'home.whySSI.universal.title',
                  descKey: 'home.whySSI.universal.desc',
                  color: 'cyan'
                },
                {
                  icon: 'devices',
                  img: '/images/home/Mobile_fast.jpg',
                  titleKey: 'home.whySSI.mobile.title',
                  descKey: 'home.whySSI.mobile.desc',
                  color: 'emerald'
                }
              ].map((item, idx) => (
                <div key={idx} className="group relative p-8 rounded-3xl bg-black/30 border border-white/5 backdrop-blur-md hover:border-primary/30 transition-all duration-500 hover:-translate-y-2">
                  <div className="size-14 flex items-center justify-center mb-6 group-hover:scale-110 transition-all duration-500">
                    <img
                      src={item.img}
                      alt={t(item.titleKey)}
                      className="w-full h-full object-contain"
                      style={{
                        maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 80%)',
                        WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 80%)',
                      }}
                    />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4 group-hover:text-primary transition-colors">{t(item.titleKey)}</h3>
                  <p className="text-white/70 text-sm leading-relaxed font-light">
                    {t(item.descKey)}
                  </p>
                  <div className={`absolute -inset-px rounded-3xl bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none`} />
                </div>
              ))}
            </div>
          </section>

          <section className="mt-16 mb-8 px-4 animate-fade-in delay-4">
            {/* [US 6.17] Section compactée (padding réduit, contenu conservé) */}
            <div className="relative overflow-hidden rounded-3xl border border-cyan-500/20 bg-gradient-to-br from-black/50 via-cyan-950/10 to-black/50 backdrop-blur-md p-6 md:p-10">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none" />
              <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 border border-primary/30 text-primary text-xs font-bold uppercase tracking-widest mb-4">
                    <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>android</span>
                    {t('home.androidApp.subtitle')}
                  </div>
                  <h2 className="text-3xl md:text-4xl font-black text-white mb-4 tracking-tight italic">
                    {t('home.androidApp.title')}
                  </h2>
                  <p className="text-white/60 font-light mb-6 leading-relaxed text-sm">
                    {t('home.androidApp.description')}
                  </p>
                  <ul className="space-y-3 mb-8">
                    {(['feature1', 'feature2', 'feature3'] as const).map(k => (
                      <li key={k} className="flex items-center gap-3 text-white/80 text-sm">
                        <span className="material-symbols-outlined text-primary flex-shrink-0" style={{ fontSize: '16px' }}>check_circle</span>
                        {t(`home.androidApp.${k}`)}
                      </li>
                    ))}
                  </ul>
                  <div className="flex items-center gap-2 text-white/40 text-xs italic">
                    <span className="material-symbols-outlined text-white/30" style={{ fontSize: '13px' }}>language</span>
                    {t('home.androidApp.webNote')}
                  </div>
                </div>

                <div className="flex flex-col items-center md:items-end gap-5">
                  <div className="text-center md:text-right">
                    {isPromo && (
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-400/15 border border-amber-400/30 rounded-full text-amber-300 text-xs font-bold uppercase tracking-wider mb-3">
                        <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>local_offer</span>
                        {t('home.androidApp.promoLabel')}
                      </div>
                    )}
                    {isPromoComing && (
                      <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-sky-400/15 border border-sky-400/30 rounded-full text-sky-300 text-xs font-bold uppercase tracking-wider mb-3">
                        <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>upcoming</span>
                        {t('home.androidApp.promoComingLabel')}
                      </div>
                    )}
                    <div className="flex items-baseline gap-3 justify-center md:justify-end">
                      <span className={`font-black tracking-tight ${isPromo ? 'text-4xl text-primary' : 'text-3xl text-white'}`}>
                        {isPromo ? t('home.androidApp.pricePromo') : t('home.androidApp.priceRegular')}
                      </span>
                      {isPromo && (
                        <span className="text-white/30 line-through text-base">{t('home.androidApp.priceRegular')}</span>
                      )}
                    </div>
                    <p className="text-white/30 text-xs mt-1">{t('home.androidApp.purchaseType')}</p>
                    {isPromoComing && (
                      <p className="text-sky-300/70 text-xs mt-2">{t('home.androidApp.promoComingDate')}</p>
                    )}
                  </div>
                  <a
                    href={GOOGLE_PLAY_HOME_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="transition-transform hover:scale-105 active:scale-95"
                  >
                    <img
                      src={`/images/Google%20Play%20Store/${GOOGLE_PLAY_BADGES_HOME[i18n.language.slice(0, 2)] ?? GOOGLE_PLAY_BADGES_HOME['en']}`}
                      alt={t('home.androidApp.cta')}
                      className="h-14 w-auto"
                    />
                  </a>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      <div className="relative z-10 w-full bg-black/40 backdrop-blur-md border-t border-white/5 py-8 mt-auto">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-12 text-center">
            {[
              { labelKey: 'home.stats.videos', val: videoCount, icon: 'videocam' },
              { labelKey: 'home.stats.subscribers', val: subscriberCount, icon: 'group' },
              { labelKey: 'home.stats.nights', val: observationNights, icon: 'nights_stay' },
              { labelKey: 'home.stats.plans', val: plannedNights, icon: 'query_stats' }
            ].map((stat, idx) => (
              <div key={idx} className="flex flex-col items-center justify-center group cursor-default">
                <div className="flex items-center gap-3 mb-2">
                  <span className="material-symbols-outlined text-primary/70 text-2xl md:text-3xl group-hover:text-primary transition-all group-hover:scale-110">{stat.icon}</span>
                  <span className="text-2xl md:text-4xl font-black text-white group-hover:text-primary transition-colors tracking-tighter">{stat.val}</span>
                </div>
                <span className="text-[10px] md:text-xs text-white/70 uppercase font-bold tracking-[0.2em]">{t(stat.labelKey)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="relative z-10">
        <Footer />
      </div>

      <NewsletterModal isOpen={isNewsletterModalOpen} onClose={() => setIsNewsletterModalOpen(false)} />

      <style>{`
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes floating {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-15px); }
        }
        .animate-fade-in {
          animation: fadeIn 0.8s ease-out forwards;
        }
        .animate-floating {
          animation: floating 6s ease-in-out infinite;
        }
        .delay-1 { animation-delay: 0.2s; }
        .delay-2 { animation-delay: 0.4s; }
        .delay-3 { animation-delay: 0.6s; }
        .delay-4 { animation-delay: 0.8s; }
        .shadow-floating {
          box-shadow: 0 30px 60px -12px rgba(0, 0, 0, 0.7), 0 18px 36px -18px rgba(0, 0, 0, 0.8);
        }
        .group:hover.shadow-floating {
          box-shadow: 0 50px 100px -20px rgba(0, 0, 0, 0.8), 0 30px 60px -30px rgba(0, 0, 0, 0.9);
        }
      `}</style>
    </div>
  );
};

export default Home;
